import { cuaWakeAssertionEnabledAtom } from '@/app-shell/rpa/cua-wake-assertion-preference';
import { useIsCuaWakeAssertionEnabled } from '@/features/flags/cua-wake-assertion-flag';
import { getDomains } from '@/helpers/getDomains';
import {
  useHasActiveWorkspaceDesktopSyncFailed,
  useIsActiveWorkspaceSyncedToDesktop,
} from '@/hooks/desktop/useSyncActiveWorkspaceToDesktop';
import { logger } from '@/utils/logger/logger';
import { bridgeErrorKind } from '@heidi/plugin-core';
import { rpaBridge } from '@heidi/plugin-rpa';
import { sanitizeError } from '@heidi/runtime/logger';
import { useWorkspaceId } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

export const useSyncCuaWakeAssertionPreference = (): void => {
  const preference = useAtomValue(cuaWakeAssertionEnabledAtom);
  const flagEnabled = useIsCuaWakeAssertionEnabled();
  const workspaceId = useWorkspaceId();
  const isWorkspaceSynced = useIsActiveWorkspaceSyncedToDesktop();
  const hasWorkspaceSyncFailed = useHasActiveWorkspaceDesktopSyncFailed();
  const enabled = flagEnabled && preference;
  // Probes stamp Heidi-Workspace-Id from Rust's store, so hold the enable until Rust
  // has the current workspace. While waiting, keep any duty the poller holds; stop it
  // once the push fails for good or the workspace clears on logout.
  const awaitingWorkspace =
    enabled && workspaceId !== null && !isWorkspaceSynced && !hasWorkspaceSyncFailed;
  const active = enabled && isWorkspaceSynced;

  useEffect(() => {
    if (!rpaBridge.isAvailable() || awaitingWorkspace) return;
    const baseUrl = getDomains().baseV2Url.replace(/\/$/, '');
    rpaBridge.setCuaWakeAssertionEnabled(active, baseUrl).catch((error) => {
      logger.warn('[cua-wake] Failed to push keep-awake preference', {
        errorKind: bridgeErrorKind(error),
        error: sanitizeError(error),
      });
    });
  }, [active, awaitingWorkspace]);
};
